"use client";
import { cn } from "@/lib/utils";
import { useState } from "react";
import DemoPanel, {
  demoGridClass,
  demoHeadingClass,
} from "../demoPanel/demoPanel.component";

type GroupTone = "lilac" | "mint" | "sky" | "sun" | "neutral";

interface ReaderCustomer {
  name: string;
  group: string;
  tone: GroupTone;
  visits: string;
  spent: string;
  lastVisit: string;
  // "What it tells you" and "What to do next".
  read: string;
  next: string;
}

export interface CustomerReaderCopy {
  listHeading: string;
  columns: string[];
  customers: ReaderCustomer[];
  readHeading: string;
  nextHeading: string;
}

interface CustomerReaderProps {
  copy: CustomerReaderCopy;
}

const toneClass: Record<GroupTone, string> = {
  lilac: "bg-tone-lilac text-tone-lilac-ink",
  mint: "bg-tone-mint text-tone-mint-ink",
  sky: "bg-tone-sky text-tone-sky-ink",
  sun: "bg-tone-sun text-tone-sun-ink",
  neutral: "bg-line text-ink-muted",
};

// Five columns from sm up; on phones the name spans the row and the rest
// wrap two to a line.
const rowGridClass =
  "grid grid-cols-2 items-center gap-2 px-3 py-2.5 text-left text-sm sm:grid-cols-[1.4fr_0.8fr_0.8fr_0.9fr_1fr]";

// The preview's `customer_reader`: a customer list where each row explains
// what the numbers say and what to do next.
const CustomerReader = ({ copy }: CustomerReaderProps) => {
  const initialState = { activeIndex: 0 };
  const [state, setState] = useState(initialState);
  const active = copy.customers[state.activeIndex];
  return (
    <div className={demoGridClass}>
      <DemoPanel className="gap-0">
        <h3 className={cn(demoHeadingClass, "mb-3")}>{copy.listHeading}</h3>
        <div
          aria-hidden="true"
          className={cn(
            rowGridClass,
            "hidden text-[11.5px] font-extrabold tracking-[0.07em] text-ink-muted uppercase sm:grid",
          )}
        >
          {copy.columns.map((column) => (
            <span key={column}>{column}</span>
          ))}
        </div>
        {copy.customers.map((customer, index) => {
          const isOn = index === state.activeIndex;
          return (
            <button
              key={customer.name}
              type="button"
              aria-pressed={isOn}
              onClick={() =>
                setState((state) => ({ ...state, activeIndex: index }))
              }
              className={cn(
                rowGridClass,
                "mt-1.5 w-full cursor-pointer rounded-2xl border-[1.5px] transition-colors duration-200 motion-reduce:transition-none",
                isOn
                  ? "border-primary bg-tone-blush"
                  : "border-line bg-white hover:border-line-warm",
              )}
            >
              <span className="col-span-full font-bold sm:col-span-1">
                {customer.name}
              </span>
              <span
                className={cn(
                  "justify-self-start rounded-full px-2.5 py-[3px] text-xs font-extrabold",
                  toneClass[customer.tone],
                )}
              >
                {customer.group}
              </span>
              <span>{customer.visits}</span>
              <span>{customer.spent}</span>
              <span>{customer.lastVisit}</span>
            </button>
          );
        })}
      </DemoPanel>
      <DemoPanel isLive>
        <h3 className={demoHeadingClass}>{copy.readHeading}</h3>
        <p className="text-base leading-normal text-ink">{active.read}</p>
        <h3 className={demoHeadingClass}>{copy.nextHeading}</h3>
        <p className="text-base leading-normal text-ink">{active.next}</p>
      </DemoPanel>
    </div>
  );
};

export default CustomerReader;
